import { AI_TASKS, type AiTask } from '@lensmira/domain'

/**
 * What Mira's endpoints send, read into types the surface can rely on. `AI-001`, `AI-005`, `AI-006`.
 *
 * Mapped rather than cast, for the reason `api/reads.ts` gives: three screens once crashed at
 * runtime on fields the server does not send, and all three typechecked. A proposal is the one
 * place that matters more than the rest, because a misread `version` is a stale answer and a
 * misread `proposed` is a person deciding about values they are not looking at.
 */

/** The reasons `AskResult.degradedReason` can carry, from `mira.service.ts`. */
export const DEGRADED_REASONS = ['degraded', 'unavailable', 'not_allowed', 'unquoted'] as const
export type DegradedReason = (typeof DEGRADED_REASONS)[number]

export interface MiraQuota {
  readonly task: AiTask
  /** `unquoted` is not "unlimited" and not "out": Mira does not do this task for the shop (`AI-006`). */
  readonly state: 'available' | 'exhausted' | 'unquoted'
  readonly remaining?: number
}

export interface MiraProposal {
  readonly id: string
  readonly task: AiTask
  /** What the screen was showing when it decides. The answer carries it back (optimistic locking). */
  readonly version: number
  readonly proposed: unknown
  readonly model: string
  readonly modelVersion: string
  /** A proportion from 0 to 1, or absent where the model gave none. Zero is not the same as absent. */
  readonly confidence?: number
}

export interface AskOutcome {
  /** Absent where nothing was proposed, which `AI-005` makes an ordinary outcome. */
  readonly proposal?: MiraProposal
  readonly degradedReason?: DegradedReason
  readonly quota: MiraQuota
}

export class MiraUnreadable extends Error {
  constructor(what: string) {
    super(`Mira's answer could not be read: ${what}.`)
    this.name = 'MiraUnreadable'
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const taskOf = (value: unknown, where: string): AiTask => {
  if (typeof value === 'string' && (AI_TASKS as readonly string[]).includes(value)) {
    return value as AiTask
  }
  throw new MiraUnreadable(`${where} is not a task`)
}

export function mapQuota(raw: unknown): MiraQuota {
  if (!isRecord(raw)) throw new MiraUnreadable('a quota row is not an object')
  const task = taskOf(raw['task'], 'quota.task')
  const state = raw['state']
  if (state !== 'available' && state !== 'exhausted' && state !== 'unquoted') {
    throw new MiraUnreadable('quota.state is not a known state')
  }
  const remaining = raw['remaining']
  return {
    task,
    state,
    ...(typeof remaining === 'number' && Number.isInteger(remaining) && remaining >= 0
      ? { remaining }
      : {}),
  }
}

export function mapQuotaList(raw: unknown): readonly MiraQuota[] {
  if (!isRecord(raw) || !Array.isArray(raw['tasks'])) {
    throw new MiraUnreadable('the quota list has no tasks')
  }
  return raw['tasks'].map(mapQuota)
}

function mapProposal(raw: unknown): MiraProposal {
  if (!isRecord(raw)) throw new MiraUnreadable('the proposal is not an object')
  const { id, version, model, modelVersion, confidence } = raw
  if (typeof id !== 'string' || id === '') throw new MiraUnreadable('the proposal has no id')
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    throw new MiraUnreadable('the proposal has no version')
  }
  return {
    id,
    task: taskOf(raw['task'], 'proposal.task'),
    version,
    proposed: raw['proposed'],
    model: typeof model === 'string' ? model : '',
    modelVersion: typeof modelVersion === 'string' ? modelVersion : '',
    ...(typeof confidence === 'number' && confidence >= 0 && confidence <= 1 ? { confidence } : {}),
  }
}

export function mapAskOutcome(raw: unknown): AskOutcome {
  if (!isRecord(raw)) throw new MiraUnreadable('the answer is not an object')
  const reason = raw['degradedReason']
  if (reason !== undefined && !(DEGRADED_REASONS as readonly unknown[]).includes(reason)) {
    throw new MiraUnreadable('degradedReason is not a known reason')
  }
  const proposal = raw['proposal']
  return {
    quota: mapQuota(raw['quota']),
    ...(proposal === undefined ? {} : { proposal: mapProposal(proposal) }),
    ...(reason === undefined ? {} : { degradedReason: reason as DegradedReason }),
  }
}
